/**
 * Keyboard behaviour for a single-choice button group
 * @module useRovingFocus
 */

import type { ShallowRef } from 'vue'

export interface RovingFocusOptions<T> {
  /** The selectable values, in the order the controls are rendered — the index the
   *  keydown handler receives is an index into this list. */
  values: () => readonly T[]
  /** Applies a selection. Selection follows focus: moving through the group only
   *  re-reads what is already on screen, so there is no second keystroke to explain. */
  select: (value: T) => void
  /** The element wrapping the group. Its controls are found by `data-roving-item`. */
  container: Readonly<ShallowRef<HTMLElement | null>>
  /** Which arrows walk the group. A horizontal tablist answers Left/Right and leaves
   *  the other axis to the page, which is what the APG defines for it; a radio group
   *  is expected to answer both axes however it is laid out. Defaults to horizontal. */
  orientation?: 'horizontal' | 'both'
}

/**
 * `role="tablist"` and `role="radiogroup"` both promise the same keyboard contract:
 * one tab stop for the whole group, and the arrows walk it once focus is inside.
 * Announcing "tab 2 of 4" and then ignoring an arrow key is worse than plain buttons,
 * so any group carrying either role gets this handler.
 *
 * The template owns the markup. Each control needs `data-roving-item`,
 * `:tabindex="isSelected ? 0 : -1"` and `@keydown="onKeydown($event, index)"`.
 *
 * @param options - Values, selection setter, the group's wrapper element and its axis
 * @returns The keydown handler to bind on every control in the group
 */
export function useRovingFocus<T>({ values, select, container, orientation = 'horizontal' }: RovingFocusOptions<T>) {
  /**
   * The arrows walk the group and wrap; Home/End jump to the ends.
   * @param event - The control's keydown event
   * @param index - Position of the control that received the key
   */
  const onKeydown = (event: KeyboardEvent, index: number) => {
    const items = values()
    const last = items.length - 1
    const bothAxes = orientation === 'both'
    let target: number

    if (event.key === 'ArrowRight' || (bothAxes && event.key === 'ArrowDown')) {
      target = index === last ? 0 : index + 1
    } else if (event.key === 'ArrowLeft' || (bothAxes && event.key === 'ArrowUp')) {
      target = index === 0 ? last : index - 1
    } else if (event.key === 'Home') {
      target = 0
    } else if (event.key === 'End') {
      target = last
    } else {
      return
    }

    const next = items[target]
    if (next === undefined) return

    event.preventDefault()
    select(next)
    // tabindex="-1" still accepts programmatic focus, so this does not have to wait
    // for the roving update to be flushed.
    container.value?.querySelectorAll<HTMLElement>('[data-roving-item]')[target]?.focus()
  }

  return { onKeydown }
}
